import React, { useState, useEffect } from 'react';
import { Nav, Button } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './Sidebar.css';

const Sidebar = () => {
  const { isAuthenticated, user, logout, isAdmin, isSuperAdmin, isSocio } = useAuth();
  const [alertasSalud, setAlertasSalud] = useState(0);

  // Contador de alertas importantes de Salud de Préstamos, visible en el menú
  // para que no dependa de que alguien entre a revisar
  useEffect(() => {
    if (!isAuthenticated || !(isAdmin() || isSuperAdmin())) return;
    axios.get('/api/salud-prestamos/resumen')
      .then(res => setAlertasSalud((res.data.alta || 0) + (res.data.media || 0)))
      .catch(() => setAlertasSalud(0));
  }, [isAuthenticated, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showSidebar, setShowSidebar] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  // Detectar cambios de tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      // En móvil, abrir todos los menús por defecto
      if (mobile) {
        setOpenMenus({
          usuarios: true,
          socios: true,
          prestamos: true,
          reportes: true,
          configuracion: true
        });
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const closeSidebar = () => setShowSidebar(false);
  const toggleSidebar = () => setShowSidebar(!showSidebar);

  const toggleMenu = (menu) => {
    // En móvil, no permitir cerrar menús (siempre abiertos)
    if (isMobile) return;
    setOpenMenus(prev => ({...prev, [menu]: !prev[menu]}));
  };

  const handleLogout = () => {
    logout();
    closeSidebar();
  };

  const handleNavClick = () => {
    // Cerrar sidebar en mobile después de hacer clic
    if (window.innerWidth < 992) {
      closeSidebar();
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Toggle button para mobile */}
      <Button 
        variant="link" 
        onClick={toggleSidebar} 
        className="sidebar-toggle d-lg-none"
      >
        <i className="fas fa-bars fs-5"></i>
      </Button>

      {/* Overlay para mobile */}
      {showSidebar && (
        <div className="sidebar-overlay d-lg-none" onClick={closeSidebar}></div>
      )}

      {/* Sidebar */}
      <aside 
        className={`sidebar ${showSidebar ? 'show' : ''} ${!isMobile && sidebarCollapsed ? 'collapsed' : ''}`}
        onMouseEnter={() => !isMobile && setSidebarCollapsed(false)}
        onMouseLeave={() => !isMobile && setSidebarCollapsed(true)}
      >
        {/* Header del Sidebar */}
        <div className="sidebar-header">
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="sidebar-brand">
              <i className="fas fa-users me-2"></i>
              <span>COPFAMI</span>
            </div>
            <Button 
              variant="link" 
              onClick={closeSidebar} 
              className="text-white d-lg-none p-0 fs-5"
            >
              <i className="fas fa-times"></i>
            </Button>
          </div>
          <div className="sidebar-subtitle">Administrador</div>
        </div>

        {/* Navigation Links */}
        <nav className="sidebar-nav">
          {/* Dashboard */}
          <LinkContainer to="/dashboard">
            <Nav.Link 
              className="sidebar-link" 
              onClick={handleNavClick}
            >
              <i className="fas fa-chart-line"></i>
              <span>Dashboard</span>
            </Nav.Link>
          </LinkContainer>

          {/* MENÚ PARA ADMIN/SUPERADMIN */}
          {(isAdmin() || isSuperAdmin()) && (
            <>
              <div className="sidebar-section-title">Administración</div>
              
              <div className="sidebar-dropdown">
                <div 
                  className="sidebar-dropdown-header"
                  onClick={() => toggleMenu('usuarios')}
                >
                  <div className="d-flex align-items-center">
                    <i className="fas fa-users"></i>
                    <span>Usuarios</span>
                  </div>
                  <i className={`fas fa-chevron-${openMenus.usuarios ? 'down' : 'right'}`}></i>
                </div>
                {openMenus.usuarios && (
                  <div className="sidebar-dropdown-content">
                    <LinkContainer to="/users">
                      <Nav.Link 
                        className="sidebar-link sidebar-sublink" 
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-users-cog"></i>
                        <span>Gestión de Usuarios</span>
                      </Nav.Link>
                    </LinkContainer>
                  </div>
                )}
              </div>

              {/* Socios - Solo para ADMIN */}
              <div className="sidebar-dropdown">
                <div 
                  className="sidebar-dropdown-header"
                  onClick={() => toggleMenu('socios')}
                >
                  <div className="d-flex align-items-center">
                    <i className="fas fa-people-group"></i>
                    <span>Socios</span>
                  </div>
                  <i className={`fas fa-chevron-${openMenus.socios ? 'down' : 'right'}`}></i>
                </div>
                {openMenus.socios && (
                  <div className="sidebar-dropdown-content">
                    <LinkContainer to="/socios">
                      <Nav.Link 
                        className="sidebar-link sidebar-sublink" 
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-address-book"></i>
                        <span>Gestión de Socios</span>
                      </Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="/socios/valores">
                      <Nav.Link
                        className="sidebar-link sidebar-sublink"
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-coins"></i>
                        <span>Gestionar Valores</span>
                      </Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="/socios/programa">
                      <Nav.Link
                        className="sidebar-link sidebar-sublink"
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-star"></i>
                        <span>Gestión de Programa</span>
                      </Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="/socios/multas">
                      <Nav.Link
                        className="sidebar-link sidebar-sublink"
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-gavel"></i>
                        <span>Multas</span>
                      </Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="/solicitudes/socios">
                      <Nav.Link 
                        className="sidebar-link sidebar-sublink" 
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-file-invoice-dollar"></i>
                        <span>Solicitudes de Socios</span>
                      </Nav.Link>
                    </LinkContainer>
                  </div>
                )}
              </div>

              {/* Asistencia - Admin puede ver todos */}
              <LinkContainer to="/asistencia">
                <Nav.Link 
                  className="sidebar-link" 
                  onClick={handleNavClick}
                >
                  <i className="fas fa-calendar-check"></i>
                  <span>Asistencia</span>
                </Nav.Link>
              </LinkContainer>

              {/* Lugares de Reunión */}
              <LinkContainer to="/lugares-reunion">
                <Nav.Link 
                  className="sidebar-link" 
                  onClick={handleNavClick}
                >
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Lugares de Reunión</span>
                </Nav.Link>
              </LinkContainer>
            </>
          )}

          {/* MENÚ PARA SOCIOS */}
          {!isAdmin() && !isSuperAdmin() && (
            <>
              <div className="sidebar-section-title">Mi Gestión</div>
              
              {/* Asistencia - Socios pueden ver suya */}
              <LinkContainer to="/asistencia">
                <Nav.Link 
                  className="sidebar-link" 
                  onClick={handleNavClick}
                >
                  <i className="fas fa-calendar-check"></i>
                  <span>Asistencia</span>
                </Nav.Link>
              </LinkContainer>
            </>
          )}

          {/* MENÚ PARA SECRETARIA */}
          {user?.role === 'secretaria' && (
            <>
              <div className="sidebar-section-title">Secretaría</div>
              
              {/* Puntos de Reunión */}
              <LinkContainer to="/puntos-reunion">
                <Nav.Link 
                  className="sidebar-link" 
                  onClick={handleNavClick}
                >
                  <i className="fas fa-clipboard-list"></i>
                  <span>Puntos de Reunión</span>
                </Nav.Link>
              </LinkContainer>

              {/* Lugares de Reunión */}
              <LinkContainer to="/lugares-reunion">
                <Nav.Link 
                  className="sidebar-link" 
                  onClick={handleNavClick}
                >
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Lugares de Reunión</span>
                </Nav.Link>
              </LinkContainer>

              {/* Informe Mensual */}
              <LinkContainer to="/informe-secretaria">
                <Nav.Link 
                  className="sidebar-link" 
                  onClick={handleNavClick}
                >
                  <i className="fas fa-file-alt"></i>
                  <span>Informe Mensual</span>
                </Nav.Link>
              </LinkContainer>

              {/* Asistencia */}
              <LinkContainer to="/asistencia">
                <Nav.Link 
                  className="sidebar-link" 
                  onClick={handleNavClick}
                >
                  <i className="fas fa-calendar-check"></i>
                  <span>Asistencia</span>
                </Nav.Link>
              </LinkContainer>
            </>
          )}

          {/* Préstamos - Menú desplegable */}
          <div className="sidebar-dropdown">
            <div 
              className="sidebar-dropdown-header"
              onClick={() => toggleMenu('prestamos')}
            >
              <div className="d-flex align-items-center">
                <i className="fas fa-file-invoice"></i>
                <span>Préstamos</span>
                {alertasSalud > 0 && !openMenus.prestamos && (
                  <span className="badge bg-danger rounded-pill ms-2">{alertasSalud}</span>
                )}
              </div>
              <i className={`fas fa-chevron-${openMenus.prestamos ? 'down' : 'right'}`}></i>
            </div>
            {openMenus.prestamos && (
              <div className="sidebar-dropdown-content">
                <LinkContainer to="/solicitudes/prestamos">
                  <Nav.Link 
                    className="sidebar-link sidebar-sublink" 
                    onClick={handleNavClick}
                  >
                    <i className="fas fa-file-invoice"></i>
                    <span>Solicitud de Préstamo</span>
                  </Nav.Link>
                </LinkContainer>
                {(isAdmin() || isSuperAdmin()) && (
                  <LinkContainer to="/plantilla-prestamo">
                    <Nav.Link
                      className="sidebar-link sidebar-sublink"
                      onClick={handleNavClick}
                    >
                      <i className="fas fa-file-alt"></i>
                      <span>Plantilla de Solicitud</span>
                    </Nav.Link>
                  </LinkContainer>
                )}
                {(isAdmin() || isSuperAdmin()) && (
                  <LinkContainer to="/salud-prestamos">
                    <Nav.Link
                      className="sidebar-link sidebar-sublink"
                      onClick={handleNavClick}
                    >
                      <i className="fas fa-heartbeat"></i>
                      <span>Salud de Préstamos</span>
                      {alertasSalud > 0 && (
                        <span className="badge bg-danger rounded-pill ms-2" title="Alertas que conviene revisar">{alertasSalud}</span>
                      )}
                    </Nav.Link>
                  </LinkContainer>
                )}
              </div>
            )}
          </div>

          {/* Reportes - Socios */}
          {isSocio() && !isAdmin() && (
            <LinkContainer to="/reportes/socio">
              <Nav.Link 
                className="sidebar-link" 
                onClick={handleNavClick}
              >
                <i className="fas fa-chart-bar"></i>
                <span>Reportes</span>
              </Nav.Link>
            </LinkContainer>
          )}

          {/* Reportes - Solo Admin/SuperAdmin */}
          {(isAdmin() || isSuperAdmin()) && (
            <>
              <div className="sidebar-dropdown">
                <div 
                  className="sidebar-dropdown-header"
                  onClick={() => toggleMenu('reportes')}
                >
                  <div className="d-flex align-items-center">
                    <i className="fas fa-chart-bar"></i>
                    <span>Reportes</span>
                  </div>
                  <i className={`fas fa-chevron-${openMenus.reportes ? 'down' : 'right'}`}></i>
                </div>
                {openMenus.reportes && (
                  <div className="sidebar-dropdown-content">
                    <LinkContainer to="/reports">
                      <Nav.Link 
                        className="sidebar-link sidebar-sublink" 
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-table"></i>
                        <span>Usuarios</span>
                      </Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="/reportes/socio-admin">
                      <Nav.Link 
                        className="sidebar-link sidebar-sublink" 
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-user-tie"></i>
                        <span>Detalle Socio</span>
                      </Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="/reportes/general">
                      <Nav.Link 
                        className="sidebar-link sidebar-sublink" 
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-file-alt"></i>
                        <span>Reporte General</span>
                      </Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="/reportes/deudas">
                      <Nav.Link
                        className="sidebar-link sidebar-sublink"
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-file-invoice-dollar"></i>
                        <span>Reporte de Deudas</span>
                      </Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="/reportes/intereses">
                      <Nav.Link
                        className="sidebar-link sidebar-sublink"
                        onClick={handleNavClick}
                      >
                        <i className="fas fa-hand-holding-usd"></i>
                        <span>Intereses de Préstamos</span>
                      </Nav.Link>
                    </LinkContainer>
                  </div>
                )}
              </div>

              {/* Configuración (Admin/SuperAdmin) */}
              <LinkContainer to="/configuration">
                <Nav.Link 
                  className="sidebar-link" 
                  onClick={handleNavClick}
                >
                  <i className="fas fa-cog"></i>
                  <span>Configuración</span>
                </Nav.Link>
              </LinkContainer>
            </>
          )}

          {/* Separador */}
          <div className="sidebar-divider"></div>

          {/* Footer links dentro del scroll */}
          <LinkContainer to="/profile">
            <Nav.Link 
              className="sidebar-link" 
              onClick={handleNavClick}
            >
              <i className="fas fa-user-circle"></i>
              <span>Mi Perfil</span>
            </Nav.Link>
          </LinkContainer>

          <Nav.Link 
            className="sidebar-link text-danger" 
            onClick={handleLogout}
          >
            <i className="fas fa-sign-out-alt"></i>
            <span>Cerrar Sesión</span>
          </Nav.Link>

          {/* User Info */}
          <div className="sidebar-user-info">
            <div className="d-flex align-items-center">
              <i className="fas fa-user-circle fa-lg me-2"></i>
              <div className="flex-grow-1">
                <small className="d-block fw-bold text-truncate">{user?.name || 'Usuario'}</small>
                <small className="text-white-50 text-truncate">{user?.email || ''}</small>
              </div>
            </div>
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
